/**
 * Service Discovery Client for Frontend Services
 * Provides dynamic service URL resolution
 */

class ServiceDiscovery {
  constructor() {
    // Try to load from localStorage first (cached from last successful fetch)
    this.serviceMap = this._loadFromCache() || {};
    
    // Default static configuration with correct ports
    this.staticConfig = {
      mcp_orchestrator: { port: 8003, host: 'localhost' },
      auth_service: { port: 8002, host: 'localhost' },
      portal_api_service: { port: 8035, host: 'localhost' },  // Fixed port
      admin_frontend_service: { port: 8040, host: 'localhost' },  // Fixed port
      public_website_service: { port: 8041, host: 'localhost' },  // Fixed port
      api_service: { port: 8000, host: 'localhost' },
      notification_service: { port: 8005, host: 'localhost' },
      medication_data_service: { port: 8006, host: 'localhost' },
      llm_service: { port: 8032, host: 'localhost' },  // Fixed port
      feedback_service: { port: 8009, host: 'localhost' },
    };
    
    // Environment-based overrides
    this.environment = import.meta.env.MODE || 'development';
    
    // Fetch latest service registry on initialization
    this.refreshServiceRegistry();
  }
  
  /**
   * Get service URL
   * @param {string} serviceName - Name of the service
   * @param {string} path - Optional path to append
   * @returns {string} Full service URL
   */
  getServiceUrl(serviceName, path = '') {
    // Check if we're running through Cloudflare tunnel
    const isTunnel = window.location.hostname === 'admin.aushadhhealth.com';
    
    if (isTunnel) {
      // When accessed through tunnel, use relative URLs
      // The tunnel routes different paths to different services
      return path || '';
    }
    
    // Check dynamic registry first
    if (this.serviceMap[serviceName]) {
      const { host, port } = this.serviceMap[serviceName];
      const baseUrl = `http://${host}:${port}`;
      return path ? `${baseUrl}${path}` : baseUrl;
    }
    
    // Check environment variables
    const envPort = import.meta.env[`VITE_${serviceName.toUpperCase()}_PORT`];
    const envHost = import.meta.env[`VITE_${serviceName.toUpperCase()}_HOST`];
    if (envPort) {
      const host = envHost || 'localhost';
      const baseUrl = `http://${host}:${envPort}`;
      return path ? `${baseUrl}${path}` : baseUrl;
    }
    
    // Fall back to static config
    if (this.staticConfig[serviceName]) {
      const { host, port } = this.staticConfig[serviceName];
      const baseUrl = `http://${host}:${port}`;
      return path ? `${baseUrl}${path}` : baseUrl;
    }
    
    // Default fallback
    console.warn(`Service ${serviceName} not found in registry`);
    return `http://localhost:8000${path}`;
  }
  
  /**
   * Get API base URL for a service
   * @param {string} serviceName - Name of the service
   * @returns {string} API base URL
   */
  getApiBaseUrl(serviceName) {
    return this.getServiceUrl(serviceName, '/api/v1');
  }
  
  /**
   * Refresh service registry from backend
   */
  async refreshServiceRegistry() {
    try {
      // Try to fetch from the frontend config endpoint first
      const configUrl = this.getServiceUrl('portal_api_service', '/api/v1/config/frontend');
      const response = await fetch(configUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // The config endpoint already returns the correct format
        this.serviceMap = data.services || {};
        this._saveToCache();
        console.log('Service registry updated from config endpoint:', this.serviceMap);
      }
    } catch (error) {
      console.warn('Failed to refresh service registry:', error);
      // Continue using cached or static config
    }
  }
  
  /**
   * Get all services
   * @returns {Object} Map of all services
   */
  getAllServices() {
    return {
      ...this.staticConfig,
      ...this.serviceMap
    };
  }
  
  /**
   * Check if a service is healthy
   * @param {string} serviceName - Name of the service
   * @returns {Promise<boolean>} True if healthy
   */
  async isServiceHealthy(serviceName) {
    try {
      const healthUrl = this.getServiceUrl(serviceName, '/health');
      const response = await fetch(healthUrl, {
        method: 'GET',
        timeout: 5000
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  }
  
  /**
   * Save service map to localStorage
   * @private
   */
  _saveToCache() {
    try {
      localStorage.setItem('dhara_service_registry', JSON.stringify({
        services: this.serviceMap,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.warn('Failed to cache service registry:', error);
    }
  }
  
  /**
   * Load service map from localStorage
   * @private
   * @returns {Object|null} Cached service map or null
   */
  _loadFromCache() {
    try {
      const cached = localStorage.getItem('dhara_service_registry');
      if (cached) {
        const data = JSON.parse(cached);
        // Cache expires after 1 hour
        if (Date.now() - data.timestamp < 3600000) {
          return data.services;
        }
      }
    } catch (error) {
      console.warn('Failed to load cached service registry:', error);
    }
    return null;
  }
}

// Create singleton instance
const serviceDiscovery = new ServiceDiscovery();

// Export for use in Vue/Pinia stores
export default serviceDiscovery;

// Helper functions for common services
export const getPortalApiUrl = (path = '') => 
  serviceDiscovery.getServiceUrl('portal_api_service', `/api/v1${path}`);

export const getMcpOrchestratorUrl = (path = '') => 
  serviceDiscovery.getServiceUrl('mcp_orchestrator', path);

export const getAuthServiceUrl = (path = '') => 
  serviceDiscovery.getServiceUrl('auth_service', path);

export const getAdminFrontendUrl = (path = '') => 
  serviceDiscovery.getServiceUrl('admin_frontend_service', path);

// Vue plugin for easy integration
export const ServiceDiscoveryPlugin = {
  install(app) {
    app.config.globalProperties.$services = serviceDiscovery;
    app.provide('serviceDiscovery', serviceDiscovery);
  }
};